import { useEffect, useState } from "react";

import { ClosedThesesPanel } from "../components/ClosedThesesPanel";
import { DecisionRow } from "../components/DecisionRow";
import { IntradayActivityPanel } from "../components/IntradayActivityPanel";
import { IntradaySetupsPanel } from "../components/IntradaySetupsPanel";
import { NavChart } from "../components/NavChart";
import { OpenPositionsPanel } from "../components/OpenPositionsPanel";
import { ApiError, api } from "../lib/api";
import { dollarsCompact, pct } from "../lib/format";
import type {
  DecisionRow as DR,
  NavRow,
  StrategyKey,
  ThesisRow,
} from "../lib/types";

type Props = {
  strategy: StrategyKey;
  onBack: () => void;
  onAuthFail: () => void;
};

function ninetyDaysAgoISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

export function StrategyDetailPage({ strategy, onBack, onAuthFail }: Props) {
  const [nav, setNav] = useState<NavRow[]>([]);
  const [decisions, setDecisions] = useState<DR[]>([]);
  const [closedTheses, setClosedTheses] = useState<ThesisRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const since = ninetyDaysAgoISO();
    const today = new Date().toISOString().slice(0, 10);
    const fetch = async () => {
      try {
        const [navRes, decRes, thRes] = await Promise.all([
          api.nav(strategy, since),
          api.decisions(strategy, today),
          api.theses(strategy, "CLOSED"),
        ]);
        if (cancelled) return;
        setNav(navRes.rows);
        setDecisions(decRes.rows);
        setClosedTheses(thRes.rows);
        setErr(null);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) onAuthFail();
        else if (!cancelled) setErr(e instanceof Error ? e.message : "failed");
      }
    };
    fetch();
    const id = window.setInterval(fetch, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [strategy, onAuthFail]);

  // Latest NAV row carries the freshest open-positions snapshot (when
  // the bot sent it). Older rows have it as null.
  const latestPositions =
    nav.length > 0 ? (nav[nav.length - 1]?.open_positions ?? null) : null;

  const first = nav[0];
  const last = nav[nav.length - 1];
  const stratReturn =
    first && last ? last.nav_cents / first.nav_cents - 1 : null;
  const spyReturn =
    first && last
      ? last.spy_close_cents / first.spy_close_cents - 1
      : null;
  const edge =
    stratReturn != null && spyReturn != null ? stratReturn - spyReturn : null;

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-4 py-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <button
            onClick={onBack}
            className="rounded border border-ink-700 px-2 py-1 text-xs text-slate-400 hover:text-slate-200"
          >
            ← overview
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50">
            {strategy}
          </h1>
        </div>
        {last && (
          <div className="font-mono text-sm text-slate-300">
            NAV {dollarsCompact(last.nav_cents)} (90d)
          </div>
        )}
      </header>

      {err && (
        <div className="rounded-md border border-accent-err bg-accent-err/10 px-3 py-2 text-sm text-accent-err">
          {err}
        </div>
      )}

      {/* Strategies without NAV records (intraday is observation-only
          until ALPACA_KEY_INTRADAY is provisioned) get an explanatory
          empty state instead of the comparison stats. */}
      {nav.length === 0 ? (
        <div className="rounded-md border border-ink-700 bg-ink-900 px-4 py-3 text-sm text-slate-400">
          <span className="font-semibold text-slate-200">
            No NAV records for this strategy yet.
          </span>{" "}
          {strategy === "intraday"
            ? "Intraday runs observation-only until ALPACA_KEY_INTRADAY is provisioned — setups still get detected and surfaced as denied decisions below."
            : "The bot may not have run a close routine yet."}
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat label="strategy 90d" value={pct(stratReturn)} />
          <Stat label="SPY 90d" value={pct(spyReturn)} />
          <Stat
            label="edge"
            value={pct(edge)}
            tone={edge == null ? "neutral" : edge >= 0 ? "ok" : "err"}
          />
        </section>
      )}

      {/* NAV chart + positions only when we have data. */}
      {nav.length > 0 && (
        <>
          <section className="rounded-lg border border-ink-700 bg-ink-900 p-3">
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-slate-200">
                NAV vs SPY (normalized to 100)
              </h2>
              <div className="text-xs text-slate-500">
                {first ? `since ${first.trading_day_et}` : ""}
              </div>
            </div>
            <NavChart rows={nav} />
          </section>

          {/* Open positions snapshot from the latest NAV row. Shows nothing
              when the bot hasn't sent positions yet (older NAV rows or
              strategies running pre-schema-upgrade). */}
          <OpenPositionsPanel
            positions={latestPositions}
            asOf={last?.trading_day_et}
          />
        </>
      )}

      {/* For the intraday strategy specifically:
          (a) Live scanner activity — what the agent sees on each fire
          (b) Setups detected today aggregated by setup_name / symbol */}
      {strategy === "intraday" && (
        <>
          <IntradayActivityPanel limit={24} />
          <IntradaySetupsPanel decisions={decisions} />
        </>
      )}

      <section className="rounded-lg border border-ink-700 bg-ink-900">
        <div className="border-b border-ink-700 px-3 py-2 text-sm font-semibold text-slate-200">
          Decisions today
          <span className="ml-2 text-xs font-normal text-slate-500">
            {decisions.length === 0
              ? "(none yet)"
              : `(${decisions.length} total)`}
          </span>
        </div>
        {decisions.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            No decisions logged today.
            <div className="mt-1 text-xs">
              latest NAV:{" "}
              {last ? (
                <span className="font-mono">{last.trading_day_et}</span>
              ) : (
                "—"
              )}
            </div>
          </div>
        ) : (
          <ul>
            {decisions.map((d) => (
              <DecisionRow key={d.id} row={d} />
            ))}
          </ul>
        )}
      </section>

      {/* Closed theses with exit-type breakdown — the "what actually
          happened" view. Only shows when the strategy has at least one
          CLOSED thesis. */}
      <ClosedThesesPanel theses={closedTheses} />
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "ok" | "err" | "neutral";
}) {
  const color =
    tone === "ok"
      ? "text-accent-ok"
      : tone === "err"
        ? "text-accent-err"
        : "text-slate-100";
  return (
    <div className="rounded-md border border-ink-700 bg-ink-900 p-3">
      <div className="text-xs uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`mt-1 font-mono text-xl ${color}`}>{value}</div>
    </div>
  );
}
